import React, { useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'react-toastify';
import { useCreateConsultationMutation } from '../../api/consultationsApi';
import { useListDoctorsQuery } from '../../api/usersApi';
import { apiErrorMessage } from '../../utils/apiError';
import ApiErrorBanner from '../../components/ApiErrorBanner';
import { doctorDisplayName } from '../appointments/doctorColors';

function defaultDateTime() {
  const d = new Date();
  d.setMinutes(0, 0, 0);
  d.setHours(d.getHours() + 1);
  const pad = (n) => String(n).padStart(2, '0');
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

/**
 * Schedule a consultation for a lead (or existing patient).
 * Props: { leadId?, patientId?, subjectName?, onClose, onCreated? }
 */
export default function ScheduleConsultationModal({ leadId, patientId, subjectName, onClose, onCreated }) {
  const init = defaultDateTime();
  const { data: doctors = [] } = useListDoctorsQuery();
  const [createConsultation, { isLoading }] = useCreateConsultationMutation();

  const [doctorUserId, setDoctorUserId] = useState('');
  const [date, setDate] = useState(init.date);
  const [time, setTime] = useState(init.time);
  const [duration, setDuration] = useState(30);
  const [type, setType] = useState('Initial');
  const [serverError, setServerError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setServerError(null);
    if (!doctorUserId) { setServerError('Please choose a doctor.'); return; }
    if (!date || !time) { setServerError('Please pick a date and time.'); return; }
    const [hh, mm] = time.split(':');
    const dt = new Date(date);
    dt.setHours(Number(hh) || 0, Number(mm) || 0, 0, 0);
    try {
      await createConsultation({
        leadId: leadId ?? null,
        patientId: patientId ?? null,
        doctorUserId,
        scheduledAt: dt.toISOString(),
        durationMinutes: Number(duration) || 30,
        type,
      }).unwrap();
      toast.success('Consultation scheduled');
      onCreated?.();
      onClose();
    } catch (err) {
      const msg = apiErrorMessage(err, 'Could not schedule consultation');
      setServerError(msg);
      toast.error(msg);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit}>
          <div className="modal-header">
            <h2>Schedule Consultation{subjectName ? ` — ${subjectName}` : ''}</h2>
            <button type="button" className="modal-close" onClick={onClose}><X size={18} /></button>
          </div>
          <div className="modal-body">
            <ApiErrorBanner message={serverError} onDismiss={() => setServerError(null)} />

            <div className="form-group">
              <label className="form-label">Doctor *</label>
              <select className="form-control" value={doctorUserId} onChange={(e) => setDoctorUserId(e.target.value)}>
                <option value="">— Select a doctor —</option>
                {doctors.map((d) => (
                  <option key={d.id} value={d.id}>
                    {doctorDisplayName(d.fullName)}{d.specialization ? ` · ${d.specialization}` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Date *</label>
                <input className="form-control" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
              </div>
              <div className="form-group">
                <label className="form-label">Time *</label>
                <input className="form-control" type="time" value={time} onChange={(e) => setTime(e.target.value)} />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Duration</label>
                <select className="form-control" value={duration} onChange={(e) => setDuration(e.target.value)}>
                  <option value={15}>15 min</option>
                  <option value={30}>30 min</option>
                  <option value={45}>45 min</option>
                  <option value={60}>60 min</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Type</label>
                <select className="form-control" value={type} onChange={(e) => setType(e.target.value)}>
                  <option value="Initial">Initial</option>
                  <option value="FollowUp">Follow-up</option>
                  <option value="Reassessment">Reassessment</option>
                </select>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-outline" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={isLoading}>
              {isLoading ? 'Scheduling…' : 'Schedule Consultation'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
